export const getCommissionCategoryFromSections = numberOfSections =>
  Number(numberOfSections) !== 1 ? 'Multi Wide' : 'Single Wide';

const getSalesLocationId = salesLocation =>
  (salesLocation?._id || salesLocation)?.toString?.() || salesLocation?.toString?.() || '';

const findCommissionConfigEntry = (appChoiceValues = [], unitLocationId, category) => {
  const locationId = unitLocationId?.toString?.() || unitLocationId || '';
  return appChoiceValues.find(
    entry =>
      getSalesLocationId(entry.salesLocation) === locationId && entry.category === category
  );
};

export const getCommissionConfigForInventory = (appChoices = [], unitLocationId, numberOfSections) => {
  const category = getCommissionCategoryFromSections(numberOfSections);
  const commissionOptionsChoice = appChoices.find(choice => choice.key === 'Commission Options');
  const commissionLessOptionsChoice = appChoices.find(choice => choice.key === 'Commission Less Options');

  const commissionEntry = findCommissionConfigEntry(
    commissionOptionsChoice?.values,
    unitLocationId,
    category
  );
  const commissionLessEntry = findCommissionConfigEntry(
    commissionLessOptionsChoice?.values,
    unitLocationId,
    category
  );

  return {
    category,
    commissionOptions: commissionEntry?.values || [],
    commissionLessOptions: commissionLessEntry?.values || [],
  };
};

export const resetCommissionFieldInFormValues = (formValues, appChoices, fieldKey) => {
  const { commissionOptions, commissionLessOptions } = getCommissionConfigForInventory(
    appChoices,
    formValues.unitLocation,
    formValues.numberOfSections
  );
  const optionDefs =
    fieldKey === 'commissionSheetLessOptions' ? commissionLessOptions : commissionOptions;

  return {
    ...formValues,
    [fieldKey]: buildCommissionSheetTableRowsFromConfig(optionDefs, []),
  };
};

export const createCommissionResetHandlers = (appChoices, setFormValues) => ({
  commissionSheetOptions: () =>
    setFormValues(prev => resetCommissionFieldInFormValues(prev, appChoices, 'commissionSheetOptions')),
  commissionSheetLessOptions: () =>
    setFormValues(prev => resetCommissionFieldInFormValues(prev, appChoices, 'commissionSheetLessOptions')),
});

export const toCommissionTableRows = (values = []) => {
  if (!values?.length) return [];
  if (Array.isArray(values[0])) {
    return values.map(row => [...row]);
  }
  return values.map(({ key, value }) => [key, value === 0 ? 0 : value ?? '']);
};

export const fromCommissionTableRows = (rows = []) =>
  rows
    .filter(row => row?.[0])
    .map(row => ({
      key: row[0],
      value: Number(row[1] ?? 0),
    }));

export const buildCommissionSheetValuesFromConfig = (optionDefs = [], existingValues = []) => {
  const existingMap = Object.fromEntries(
    toCommissionTableRows(existingValues).map(([key, value]) => [key, value])
  );

  return optionDefs.map(({ name, defaultValue }) => ({
    key: name,
    value:
      existingMap[name] !== undefined && existingMap[name] !== null && existingMap[name] !== ''
        ? Number(existingMap[name])
        : Number(defaultValue ?? 0),
  }));
};

export const buildCommissionSheetTableRowsFromConfig = (optionDefs = [], existingValues = []) =>
  buildCommissionSheetValuesFromConfig(optionDefs, existingValues).map(({ key, value }) => [
    key,
    value === 0 ? 0 : value ?? '',
  ]);

export const applyCommissionValuesToFormValues = (formValues = {}) => ({
  ...formValues,
  commissionSheetOptions: toCommissionTableRows(formValues.commissionSheetOptions),
  commissionSheetLessOptions: toCommissionTableRows(formValues.commissionSheetLessOptions),
});

export const applyCommissionDefaultsToFormValues = (
  formValues,
  appChoices,
  { preserveExisting = false } = {}
) => {
  const { commissionOptions, commissionLessOptions } = getCommissionConfigForInventory(
    appChoices,
    formValues.unitLocation,
    formValues.numberOfSections
  );

  return {
    ...formValues,
    commissionSheetOptions: buildCommissionSheetTableRowsFromConfig(
      commissionOptions,
      preserveExisting ? formValues.commissionSheetOptions : []
    ),
    commissionSheetLessOptions: buildCommissionSheetTableRowsFromConfig(
      commissionLessOptions,
      preserveExisting ? formValues.commissionSheetLessOptions : []
    ),
  };
};

export const normalizeCommissionSheetPayload = values => fromCommissionTableRows(toCommissionTableRows(values));

export const getDefaultMockCommissionFields = (appChoices = []) => {
  const salesLocations = appChoices.find(choice => choice.key === 'salesLocation')?.values || [];
  const unitLocationId = salesLocations[0]?._id;
  const { commissionOptions, commissionLessOptions } = getCommissionConfigForInventory(
    appChoices,
    unitLocationId,
    1
  );

  return {
    invoice: 0,
    commissionSheetOptions: buildCommissionSheetValuesFromConfig(commissionOptions, []),
    commissionSheetLessOptions: buildCommissionSheetValuesFromConfig(commissionLessOptions, []),
  };
};
